/* eslint-disable camelcase */
import {createSlice} from '@reduxjs/toolkit';

import * as unitsApi from '../api/admin/units';
import * as activeClassApi from '../api/activeClass';

const unitsSlice = createSlice({
  initialState: {
    error: null,
    filterQualifications: null,
    isListLoading: true,
    isUnitLoading: false,
    currentQualification: null,
    units: null,
    unitDetails: null,
    isLoadingUnitDetails: false,
    assessmentLoading: false,
    assessmentError: null,
    isDeleteLoading: false,
    activityId: null,
    searchTerm: '',
    createdAssessment: {},
    createdTasks: [],
    draftTask: null,
    performanceCriteria: null,
    knowledgeEvidences: null,
    performanceEvidences: null,
    assessmentConditions: null,
    assessmentType: [],
    evaluationType: [],
    assessmentTasksError: '',
    assessmentTasksLoading: false,
  },
  name: 'units',
  reducers: {
    editUnit(state, action) {
      const unitIndex = state.units.findIndex(
        unit => unit.ou_id_orgunit === action.payload.ou_id_orgunit,
      );
      state.units[unitIndex] = action.payload;
    },
    removeUnitById(state, action) {
      state.units = state.units.filter(
        unit => unit.ou_id_unit !== action.payload,
      );
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setFilterQualifications(state, action) {
      state.filterQualifications = action.payload;
    },
    setCurrentQualifications(state, action) {
      state.currentQualification = action.payload;
    },
    setIsListLoading(state, action) {
      state.isListLoading = action.payload;
    },
    setAssessmentTasksError: (state, action) => {
      state.assessmentTasksError = action.payload;
    },
    setAssessmentTasksLoading: (state, action) => {
      state.assessmentTasksLoading = action.payload;
    },
    setIsUnitLoading(state, action) {
      state.isUnitLoading = action.payload;
    },
    setUnits(state, action) {
      state.units = action.payload;
    },
    setUnitDetails: (state, action) => {
      state.unitDetails = action.payload;
    },
    updateUnitDetails: (state, action) => {
      state.unitDetails.unitRelease = action.payload.unitRelease;
      state.unitDetails.assessments = state.unitDetails.assessments.filter(
        assessment => assessment.ac_id_activity !== action.payload.assessmentId,
      );
    },
    clearUnitDetails: state => {
      state.unitDetails = null;
    },
    setIsLoadingUnitDetails: (state, action) => {
      state.isLoadingUnitDetails = action.payload;
    },
    setAssessmentLoading: (state, action) => {
      state.assessmentLoading = action.payload;
    },
    setAssessmentError: (state, action) => {
      state.assessmentError = action.payload;
    },
    setIsDeleteLoading: (state, action) => {
      state.isDeleteLoading = action.payload;
    },
    setActivityId: (state, action) => {
      state.activityId = action.payload;
    },
    setSearchField: (state, action) => {
      state.searchTerm = action.payload;
    },
    fetchCreatedAssessment: (state, action) => {
      state.createdAssessment = action.payload;
    },
    fetchUpdatedAssessment: (state, action) => {
      state.createdAssessment = action.payload;
    },
    resetAction: state => {
      state.createdTasks = [];
      state.createdFiles = [];
      state.createdAssessment = {};
    },
    setAssessmentTask: (state, action) => {
      const {
        ai_id_activityitem,
        ai_id_activityitemtype,
        ai_activityitem,
        ai_activityitem_description,
        ai_is_published,
        ai_id_evaltype,
        aipc_id_tcriteria,
        aipe_id_tpevidence,
        aike_id_tkevidence,
        aiac_id_tacondition,
        tpc_id_tgovelement,
        tpc_criteria_id,
        tpc_criteria_value,
        et_eval_name,
        performanceCriteria,
        performanceEvidence,
        knowledgeEvidence,
        assessmentCondition,
        linkedFiles,
        isEdit,
      } = action.payload;
      let taskType;
      switch (ai_id_activityitemtype) {
        case 1:
          taskType = 'Image';
          break;
        case 2:
          taskType = 'Video';
          break;
        case 3:
          taskType = 'Document';
          break;
        default:
          taskType = '';
      }

      const task = {
        id: ai_id_activityitem,
        type: taskType,
        name: ai_activityitem,
        description: ai_activityitem_description,
        isPublished: ai_is_published,
        taskFiles: linkedFiles,
        evaluationType: ai_id_evaltype,
        evaluationName: et_eval_name,
        performanceCriteriaId: performanceCriteria
          ? performanceCriteria.tpc_id_tgovperformancecriteria
          : aipc_id_tcriteria,
        // eslint-disable-next-line no-nested-ternary
        performanceCriteriaName: performanceCriteria
          ? `${performanceCriteria.tpc_criteria_id} ${performanceCriteria.tpc_criteria_value}`
          : tpc_criteria_id
          ? `${tpc_criteria_id} ${tpc_criteria_value}`
          : null,
        elementId: performanceCriteria
          ? performanceCriteria.tpc_id_tgovelement
          : tpc_id_tgovelement,
        performanceEvidenceId:
          performanceEvidence?.tpe_id_tgovperformanceevidence ||
          aipe_id_tpevidence,
        performanceEvidenceName: performanceEvidence
          ? performanceEvidence.tpe_evidence_value
          : action.payload?.tpe_evidence_value,
        knowledgeEvidenceId: knowledgeEvidence
          ? knowledgeEvidence.tke_id_tgovknowledgeevidence
          : aike_id_tkevidence,
        knowledgeEvidenceName: knowledgeEvidence
          ? knowledgeEvidence?.tke_evidence_value
          : action.payload?.tke_evidence_value,
        assessmentConditionId:
          assessmentCondition?.tac_id_tgovassessmentcondition ||
          aiac_id_tacondition,
        assessmentConditionName: assessmentCondition
          ? assessmentCondition.tac_condition
          : action?.payload?.tac_condition,
      };

      if (isEdit) {
        const indexOfTaskToReplace = state.createdTasks.findIndex(
          item => item.id === task.id,
        );
        state.createdTasks[indexOfTaskToReplace] = task;
      } else {
        state.createdTasks.push(task);
      }
    },
    setAssessmentFiles: (state, action) => {
      const {fi_id_file, fi_filename, fi_filesize, fi_location} =
        action.payload;

      state.createdFiles.push({
        id: fi_id_file,
        name: fi_filename,
        size: fi_filesize,
        location: fi_location,
      });

      // Removing if any duplicated array of obj found
      const file = [
        ...new Map(
          state.createdFiles.map(obj => [JSON.stringify(obj), obj]),
        ).values(),
      ];

      state.createdFiles = file;
    },
    setAssessmentUnitId: (state, action) => {
      state.assessmentUnitId = action.payload;
    },
    setDraftTask: (state, action) => {
      state.draftTask = action.payload;
    },
    updateLinkedFile: (state, action) => {
      const {file, linkId, taskId} = action.payload;
      let fileLink;

      if (taskId) {
        const task = state.createdTasks.find(item => item.id === taskId);
        fileLink = task.taskFiles.find(
          linkedFile => linkedFile.fl_id_filelink === linkId,
        );
      } else {
        fileLink = state.draftTask.linkedFiles.find(
          linkedFile => linkedFile.fl_id_filelink === linkId,
        );
      }

      fileLink.file = file;
    },
    clearTaskInstruction: (state, action) => {
      const {fileID, idOfTaskToEdit, taskID} = action.payload;
      let task;

      if (idOfTaskToEdit) {
        task = state.createdTasks.find(item => item.id === idOfTaskToEdit);
        task.taskFiles = task.taskFiles.filter(
          item => item.fl_id_filelink !== fileID,
        );
      } else {
        task = state.createdTasks.find(item => item.id === taskID);

        if (task) {
          task.taskFiles = task.taskFiles.filter(
            item => item.fl_id_filelink !== fileID,
          );
        } else {
          state.draftTask.linkedFiles = state.draftTask.linkedFiles.filter(
            item => item.fl_id_filelink !== fileID,
          );
        }
      }
    },
    clearAllTaskInstructions: state => {
      state.draftTask.linkedFiles = [];
    },
    setLinkedFile: (state, action) => {
      const {file, taskId} = action.payload;

      if (taskId) {
        const task = state.createdTasks.find(item => item.id === taskId);
        task.taskFiles.push(file);
      } else {
        state.draftTask.linkedFiles.push(file);
      }
    },
    setPerformanceCriteria: (state, action) => {
      state.performanceCriteria = action.payload;
    },
    setPerformanceEvidences: (state, action) => {
      state.performanceEvidences = action.payload;
    },
    setKnowledgeEvidences: (state, action) => {
      state.knowledgeEvidences = action.payload;
    },
    setAssessmentConditions: (state, action) => {
      state.assessmentConditions = action.payload;
    },
    setAssessmentType: (state, action) => {
      state.assessmentType = action.payload;
    },
    setEvaluationType: (state, action) => {
      state.evaluationType = action.payload;
    },
    deleteSelectedTaskItem: (state, action) => {
      state.createdTasks = state.createdTasks.filter(
        task => task.id !== action.payload,
      );
    },
  },
});

export const {
  editUnit,
  removeUnitById,
  setError,
  setFilterQualifications,
  setIsListLoading,
  setIsUnitLoading,
  setUnits,
  setCurrentQualifications,
  setUnitDetails,
  updateUnitDetails,
  clearUnitDetails,
  setIsLoadingUnitDetails,
  setAssessmentLoading,
  setIsDeleteLoading,
  setAssessmentTasksError,
  setAssessmentTasksLoading,
  setAssessmentError,
  setSearchField,
  setActivityId,
  resetAction,
  fetchCreatedAssessment,
  fetchUpdatedAssessment,
  setAssessmentTask,
  setAssessmentFiles,
  setAssessmentUnitId,
  setDraftTask,
  updateLinkedFile,
  clearTaskInstruction,
  setLinkedFile,
  clearAllTaskInstructions,
  setPerformanceCriteria,
  setKnowledgeEvidences,
  setAssessmentConditions,
  setPerformanceEvidences,
  setEvaluationType,
  setAssessmentType,
  deleteSelectedTaskItem,
} = unitsSlice.actions;

export const deleteUnit =
  (qualificationId, unitId, callback) => async dispatch => {
    dispatch(setIsUnitLoading(true));
    try {
      await unitsApi.deleteUnit(qualificationId, unitId);
      dispatch(removeUnitById(unitId));

      if (callback) {
        callback();
      }
    } catch (error) {
      dispatch(
        setError(error?.response?.data?.message || 'Something went wrong.'),
      );
    } finally {
      dispatch(setIsUnitLoading(false));
    }
  };

export const fetchAllFilterQualifications = () => async dispatch => {
  try {
    const filterQualificationsResponse =
      await unitsApi.fetchAllFilterQualifications();
    dispatch(
      setFilterQualifications(
        filterQualificationsResponse.data?.allQualificationsList || [],
      ),
    );
  } catch (error) {
    dispatch(
      setError(error?.response?.data?.message || 'Something went wrong.'),
    );
  }
};

export const fetchAllUnits = qualificationId => async dispatch => {
  try {
    const unitsResponse = await unitsApi.fetchAllUnits(qualificationId);
    dispatch(setUnits(unitsResponse.data?.allUnits || []));
  } catch (error) {
    dispatch(
      setError(error?.response?.data?.message || 'Something went wrong.'),
    );
  } finally {
    dispatch(setIsListLoading(false));
  }
};

export const updateUnit = (unitId, data, callback) => async dispatch => {
  dispatch(setIsUnitLoading(true));

  try {
    const response = await unitsApi.updateUnit(unitId, {
      unitCode: data.unitCode,
      unitName: data.unitName,
      unitType: data.unitType,
    });

    if (response.data?.editUnit) {
      dispatch(editUnit(response.data?.editUnit));

      if (callback) {
        callback();
      }
    }
  } catch (error) {
    dispatch(
      setError(error?.response?.data?.message || 'Something went wrong.'),
    );
  } finally {
    dispatch(setIsUnitLoading(false));
  }
};

/** Fetch details of one unit by its ID. */
export const fetchOneUnitById = unitId => async dispatch => {
  try {
    dispatch(setIsLoadingUnitDetails(true));
    const {
      data: {unit},
    } = await unitsApi.getUnitById(unitId);
    dispatch(setUnitDetails(unit));
  } finally {
    dispatch(setIsLoadingUnitDetails(false));
  }
};

export const setAssessmentTitle =
  (unitId, callBack) => async (dispatch, getState) => {
    dispatch(setAssessmentLoading(true));
    dispatch(setAssessmentError(false));
    try {
      const {searchTerm: title} = getState().units;

      const resp = await activeClassApi.createAssessmentByTitle(unitId, title);

      const activityId = resp.data.activity.ac_id_activity;

      // dispatch(fetchCreatedAssessment(resp.data.activity));
      dispatch(setActivityId(activityId));

      dispatch(setSearchField('')); // cleaning searchField

      callBack(activityId);
    } catch (err) {
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(true));
    } finally {
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(false));
    }
  };

export const updateAssessmentDetails =
  (activityId, data, callBack) => async dispatch => {
    try {
      dispatch(setAssessmentLoading(true));
      const resp = await activeClassApi.updateAssessment(activityId, data);
      dispatch(fetchUpdatedAssessment(resp.data.activity));
      dispatch(setAssessmentUnitId(data.unitId));
      if (callBack) {
        callBack();
      }
    } catch (err) {
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(err));
    } finally {
      dispatch(setAssessmentLoading(false));
    }
  };

export const getAssessmentDetails =
  (activityId, history, callback) => async dispatch => {
    dispatch(setAssessmentLoading(true));

    try {
      const resp = await activeClassApi.getAssessmentDatas(activityId);
      if (callback) callback();
      dispatch(setActivityId(activityId));
      dispatch(fetchCreatedAssessment(resp.data.assessments));
      const availableTask = resp.data.assessments.tasks.filter(
        task => task.ai_is_deleted !== 1,
      );
      availableTask.forEach(task => {
        dispatch(setAssessmentTask(task));
      });
      const availableFiles = resp.data.assessments.files.filter(
        file => file.fi_is_deleted !== 1,
      );
      availableFiles.forEach(file => {
        dispatch(setAssessmentFiles(file));
      });
      if (history) {
        // history.push(
        //   `/stream/${classId}/assessment/create/${activityId}/#details`,
        // );
      }
    } catch (err) {
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(err));
    } finally {
      dispatch(setAssessmentLoading(false));
    }
  };

export const fetchDraftTask = activityID => async dispatch => {
  const {data} = await activeClassApi.getDraftTask(activityID);
  dispatch(setDraftTask(data.task));
};

export const addTextInstruction =
  (activityID, taskID, textInstruction, callback, idOfTaskToEdit) =>
  async dispatch => {
    const {data} = await activeClassApi.addTextInstruction(
      activityID,
      taskID,
      textInstruction,
    );

    if (callback) {
      callback(data.linkedFile.fl_id_filelink);
    }

    dispatch(
      setLinkedFile({
        file: data.linkedFile,
        taskId: idOfTaskToEdit,
      }),
    );
  };

export const updateInstruction =
  (fileID, body, taskId, callback, idOfTaskToEdit) => async dispatch => {
    const {data} = await activeClassApi.updateInstruction(fileID, body);
    dispatch(
      updateLinkedFile({
        file: data.linkedFile.file,
        linkId: data.linkedFile.fl_id_filelink,
        taskId: idOfTaskToEdit,
      }),
    );

    if (callback) {
      callback(fileID, data.linkedFile);
    }
  };

export const removeInstruction =
  (taskID, fileID, idOfTaskToEdit, callback) => async dispatch => {
    await activeClassApi.removeInstruction(fileID);
    dispatch(
      clearTaskInstruction({
        taskID,
        fileID,
        idOfTaskToEdit,
      }),
    );

    if (callback) {
      callback();
    }
  };

export const getPerformanceCriteria = unitId => async dispatch => {
  const {
    data: {
      assessmentMap: {elements: performanceCriteria},
    },
  } = await activeClassApi.getPerformanceCriteria(unitId);

  dispatch(setPerformanceCriteria(performanceCriteria));
};

/** Fetch the performance evidence of a unit. */
export const fetchPerformanceEvidence = unitId => async dispatch => {
  const resp = await activeClassApi.getPerformanceEvidence(unitId);
  dispatch(setPerformanceEvidences(resp.data.performanceEvidence));
};

/** Fetch the knowledge evidence of a unit. */
export const fetchKnowledgeEvidence = unitId => async dispatch => {
  const resp = await activeClassApi.getKnowledgeEvidence(unitId);
  dispatch(setKnowledgeEvidences(resp.data.knowledgeEvidence));
};

/** Fetch the knowledge evidence of a unit. */
export const fetchAssessmentCondition = unitId => async dispatch => {
  const resp = await activeClassApi.getAssessmentCondition(unitId);
  dispatch(setAssessmentConditions(resp.data.assessmentCondition));
};

export const getAllActivityMethods = () => async dispatch => {
  try {
    const resp = await activeClassApi.fetchAllActivityMethods();
    dispatch(setAssessmentType(resp.data.allActivityMethods));
  } catch (err) {
    dispatch(setError(true));
  }
};

export const getAllEvaluationType = () => async dispatch => {
  try {
    const resp = await activeClassApi.fetchAllEvaluationType();
    dispatch(setEvaluationType(resp.data.allEvaluationType));
  } catch (err) {
    dispatch(setError(err));
  }
};

export const updateTask =
  (taskID, body, isPublished, isEdit, callback) => async dispatch => {
    dispatch(setAssessmentTasksLoading(true));
    dispatch(setAssessmentLoading(true));
    dispatch(setAssessmentError(false));

    try {
      const {data} = await activeClassApi.updateTask(taskID, {
        ...body,
        isPublished,
      });
      dispatch(setDraftTask(null));
      dispatch(setAssessmentTasksLoading(false));
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(false));
      dispatch(setAssessmentTask({...data.task, isEdit}));

      if (callback) {
        callback();
      }
    } catch (error) {
      dispatch(setAssessmentTasksLoading(false));
      dispatch(setAssessmentTasksError(error.message));
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(true));
    }
  };

export const removeAllInstructions = taskID => async dispatch => {
  await activeClassApi.removeAllInstructions(taskID);
  dispatch(clearAllTaskInstructions());
};

export const addFileInstruction =
  (activityID, taskID, body, callback, idOfTaskToEdit) => async dispatch => {
    const {data} = await activeClassApi.addFileInstruction(
      activityID,
      taskID,
      body,
    );
    if (callback) callback(data.linkedFile.fl_id_filelink);
    dispatch(
      setLinkedFile({
        file: data.linkedFile,
        taskId: idOfTaskToEdit,
      }),
    );
  };

export const onDeletingAssessmentTask =
  (activityItemId, isQueued, callback) => async dispatch => {
    dispatch(setAssessmentLoading(true));
    dispatch(setAssessmentError(false));

    try {
      if (!isQueued) {
        const resp = await activeClassApi.deleteAssessmentTask(activityItemId);
        if (resp.data.draftTask) {
          dispatch(setDraftTask(resp.data.draftTask));
        }
      }

      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(false));

      if (!Array.isArray(activityItemId)) {
        dispatch(deleteSelectedTaskItem(activityItemId));
      }
      if (callback) {
        callback();
      }
    } catch (err) {
      dispatch(setAssessmentLoading(false));
      dispatch(setAssessmentError(true));
    }
  };

export const deleteAssessment =
  (activityID, callback, unitId) => async dispatch => {
    try {
      dispatch(setIsDeleteLoading(true));
      await activeClassApi.deleteAssessment(activityID);

      if (callback) {
        callback(activityID);
      }
      if (unitId) {
        const {
          data: {unit},
        } = await unitsApi.getUnitById(unitId);
        dispatch(
          updateUnitDetails({
            assessmentId: activityID,
            unitRelease: unit?.unitRelease,
          }),
        );
      }
    } catch (error) {
      dispatch(setAssessmentError(true));
      dispatch(setIsDeleteLoading(false));
    } finally {
      dispatch(setIsDeleteLoading(false));
    }
  };

export const unitsSelector = state => state.units;

export default unitsSlice.reducer;
